import { Box, Group } from '@mantine/core';
import { IconCheck, IconChevronLeft, IconChevronRight, IconCloudUpload } from '@tabler/icons-react';
import Button from 'components/Button';
import { useFillOutRevisionSaveDraftProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionSaveDraftProvider';
import { useMemo } from 'react';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

export interface RevisionFormFooterProps {
  revisionKind: 'revision' | 'form';
  loading: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  canSubmit: boolean;
  onPrevStep: () => void;
  onNextStep: () => void;
  onSave: () => void;
  onSubmit: () => void;
}

/**
 * The revision form footer. Allows to submit or cancel the form.
 */
export default function RevisionFormFooter({
  revisionKind,
  loading,
  isFirstStep,
  isLastStep,
  canSubmit,
  onPrevStep,
  onNextStep,
  onSave,
  onSubmit,
}: RevisionFormFooterProps) {
  const { isMobile, isTablet } = useResponsiveDesign();
  const { autoSaving, lastSavedTimestamp } = useFillOutRevisionSaveDraftProvider();
  const savedInLast3Seconds = lastSavedTimestamp > Date.now() - 3000;

  const saveDraftText = useMemo(() => {
    if (savedInLast3Seconds) {
      return 'Uložené';
    }

    if (autoSaving) {
      return 'Prebieha automatické ukladanie …';
    }

    return 'Uložiť';
  }, [autoSaving, savedInLast3Seconds]);

  const SaveDraftIcon = useMemo(() => (savedInLast3Seconds ? IconCheck : IconCloudUpload), [savedInLast3Seconds]);

  return (
    <Box
      h={isMobile ? 'auto' : 72}
      w="100%"
      py={16}
      px={isMobile ? 16 : isTablet ? 20 : 40}
      sx={() => ({
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px',
        boxShadow: '0px -4px 12px 0px rgba(197, 198, 211, 0.40)',
      })}
    >
      <Group position={isMobile ? 'left' : 'apart'} noWrap={!isMobile} align={isMobile ? 'stretch' : 'center'}>
        <Button
          variant="secondary"
          leftIcon={<SaveDraftIcon stroke="1.5" height={24} width={24} />}
          size="md"
          loading={loading}
          fullWidth={isMobile}
          onClick={onSave}
        >
          {saveDraftText}
        </Button>
        <Group spacing={16} w={isMobile ? '100%' : undefined} noWrap={!isMobile}>
          <Button
            variant="subtle-gray"
            leftIcon={<IconChevronLeft stroke="1.5" height={24} width={24} />}
            size="md"
            loading={loading}
            loaderPosition="center"
            loaderProps={{ size: 0 }}
            disabled={isFirstStep}
            fullWidth={isMobile}
            onClick={onPrevStep}
          >
            Späť
          </Button>
          {isLastStep && canSubmit ? (
            <Button
              type="button"
              variant="primary"
              rightIcon={<IconChevronRight stroke="1.5" height={24} width={24} />}
              size="md"
              loading={loading}
              loaderPosition="center"
              loaderProps={{ size: 0 }}
              fullWidth={isMobile}
              onClick={onSubmit}
            >
              Ukončiť a odovzdať
            </Button>
          ) : !isLastStep ? (
            <Button
              variant="subtle"
              rightIcon={<IconChevronRight stroke="1.5" height={24} width={24} />}
              size="md"
              loading={loading}
              loaderPosition="center"
              loaderProps={{ size: 0 }}
              fullWidth={isMobile}
              onClick={onNextStep}
            >
              Pokračovať ďalej
            </Button>
          ) : (
            <></>
          )}
        </Group>
      </Group>
    </Box>
  );
}
